import { Component, effect, Input } from '@angular/core';
import { ControlService } from 'src/app/pages/learner/call/library/control.service';
import { PlayerService } from 'src/app/pages/learner/call/library/player.service';
import { CommonModule } from '@angular/common';
import { QaiCallAudioIndicatorComponent, QaiImageComponent } from '@qai/ui';
import { CallFlagService } from 'src/app/services/learner/call-flag/call-flag.service';
import { Subject } from 'rxjs';
import { VisualAidNextPageNavigatorComponent } from '../visual-aid-next-page-navigator/visual-aid-next-page-navigator.component';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { TranslocoModule } from '@jsverse/transloco';
import { RealtimeService } from '../../library/realtime/realtime.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { LoggerService } from 'src/app/services/logger/logger.service';

@Component({
  imports: [
    CommonModule,
    QaiCallAudioIndicatorComponent,
    QaiImageComponent,
    VisualAidNextPageNavigatorComponent,
    TranslocoModule,
  ],
  selector: 'app-visual-guide',
  standalone: true,
  templateUrl: './visual-guide.component.html',
  styleUrls: ['./visual-guide.component.scss'],
})
export class VisualGuideComponent {
  @Input() controlService: ControlService | undefined;
  @Input() playerService: PlayerService | undefined;
  private destroy$ = new Subject<void>();
  visualAidImageUrl: string = '';

  constructor(
    public callFlagService: CallFlagService,
    public callStore: CallStoreService,
    private realTimeService: RealtimeService,
    private amplitudeService: AmplitudeService,
    private logger: LoggerService
  ) {
    effect(() => {
      this.visualAidImageUrl = this.callStore.getVisualAidImageUrl();
    });
  }

  private handleNextClip(): void {
    this.callStore.setIsContinueButtonBlocked(true);
    this.callStore.setIsSendingAnswer(true);
    this.playerService?.nextClipVisualAidV2();
  }

  protected get showContinueButton() {
    return !this.callStore.getIsCallComplete();
  }

  protected get canContinueNext() {
    if (!this.controlService || !this.playerService) {
      return false;
    }

    const isPaused = this.callStore.getIsPaused();
    const isBlocked = this.callStore.getIsContinueButtonBlocked();
    const hasTranscript = this.callStore.getSpeechTranscript().length !== 0 ||
                         this.callStore.getSpeechTranscriptPartial().length !== 0;
    const isProcessing = this.callStore.getIsProcessingAnswer();
    const coreConditions = !isPaused && !isBlocked && hasTranscript && !isProcessing;
    if (!coreConditions) {
      return false;
    }
    // For real-time calls, check if we can continue to next slide
    if (this.controlService?.isRealTimeCall) {
      const canContinueToNextSlide = this.canContinueToNextSlide();
      return coreConditions && canContinueToNextSlide;
    }
    // For non-real-time calls
    return true;
  }

  protected handleContinueClick() {
    if (this.controlService?.displayTour) {
      return;
    }
    if (this.canContinueNext) {
      const isRealTimeCall = this.controlService?.isRealTimeCall;
      if (isRealTimeCall) {
        this.realTimeService?.handleClickContinue();
      } else {
        this.handleNextClip();
      }
    } else {
      this.controlService?.clickOpenNoResponse();
    }
  }

  get isLoading(): boolean {
    return !this.visualAidImageUrl || this.visualAidImageUrl === '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleFullScreenClick() {
    // Track the full screen toggle event using Amplitude
    const eventName = this.callStore.getIsFullScreen()
      ? AmplitudeTrackingEvents.CALL_CLICK_COLLAPSE_VISUAL_AID
      : AmplitudeTrackingEvents.CALL_CLICK_FULLSCREEN_VISUAL_AID;

    // Get the current visual aid index
    const clipIndex = this.playerService?.currentClipIndex ?? 0;
    const clip = this.playerService?.callClips[clipIndex];
    const currentVisualIndex = clip?.visualAidIndex ?? null;

    this.amplitudeService.trackEvent(eventName, {
      image_index: currentVisualIndex,
      image_url: this.visualAidImageUrl,
      call_uuid: this.controlService?.callUuid,
      practice_call_uuid: this.controlService?.practiceCallUuid,
    });

    // Toggle the full screen state in the call store
    const isFullScreen = this.callStore.getIsFullScreen();
    this.callStore.setIsFullScreen(!isFullScreen);

    // Toggle browser fullscreen based on the new state
    if (!isFullScreen) {
      this.logger.log('[CALL] full screen view opened - Visual Aid');
      this.requestFullscreen();
    } else {
      this.logger.log('[CALL] full screen view closed - Visual Aid');
      this.exitFullscreen();
    }
  }

  requestFullscreen(element: HTMLElement = document.documentElement): void {
    const request =
      element.requestFullscreen ||
      (element as any).webkitRequestFullscreen ||
      (element as any).msRequestFullscreen;

    if (request) {
      request.call(element);
    }
  }

  exitFullscreen(): void {
    const exit =
      document.exitFullscreen ||
      (document as any).webkitExitFullscreen ||
      (document as any).msExitFullscreen;

    if (exit) {
      exit.call(document);
    }
  }

  private canContinueToNextSlide(): boolean {
    if (!this.realTimeService?.guidedClips || this.realTimeService?.guidedIndex === undefined || this.realTimeService?.guidedIndex === null) {
      return false;
    }

    const { guidedClips, guidedIndex } = this.realTimeService;
    if (guidedIndex >= guidedClips.length - 1) {
      return false;
    }
    return true;
  }
}
